import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'
import permissionService from '../services/permissions'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue'),
    meta: { requiresAuth: true, page: 'dashboard' }
  },
  {
    path: '/favorites',
    name: 'Favorites',
    component: () => import('../views/Favorites.vue'),
    meta: { requiresAuth: true, page: 'favorites' }
  },
  {
    path: '/users',
    name: 'Users',
    component: () => import('../views/Users.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/manage-reports',
    name: 'ManageReports',
    component: () => import('../views/ManageReports.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/report-access',
    name: 'ReportAccess',
    component: () => import('../views/ReportAccess.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/meta',
    name: 'Meta',
    component: () => import('../views/Meta.vue'),
    meta: { requiresAuth: true, page: 'meta' }
  },
  {
    path: '/meta/detalhe/:canalId/:ano/:grupoId',
    name: 'MetaDetail',
    component: () => import('../views/MetaDetail.vue'),
    meta: { requiresAuth: true, page: 'meta' }
  },
  {
    path: '/meta/editar/:channelId',
    name: 'GoalEdit',
    component: () => import('../views/GoalEdit.vue'),
    meta: { requiresAuth: true, page: 'meta' }
  },
  {
    path: '/test-embed',
    name: 'TestEmbed',
    component: () => import('../components/PowerBITestViewer.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true, page: 'profile' }
  },
  {
    path: '/force-change-password',
    name: 'ForceChangePassword',
    component: () => import('../views/ForceChangePassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/auto-login',
    name: 'AutoLogin',
    component: () => import('../views/AutoLogin.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/primeiro-acesso',
    name: 'FirstAccess',
    component: () => import('../views/FirstAccessChangePassword.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/page-management',
    name: 'PageManagement',
    component: () => import('../views/PageManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/page-management/franquias-tabs',
    name: 'PageManagementFranquiasTabs',
    component: () => import('../views/PageManagementFranquiasTabs.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/representacao',
    name: 'Representacao',
    component: () => import('../views/Representacao.vue'),
    meta: { requiresAuth: true, page: 'representacao' }
  },
  {
    path: '/relatorios',
    name: 'Relatorios',
    component: () => import('../views/Reports.vue'),
    meta: { requiresAuth: true, page: 'reports_list' }
  },
  {
    path: '/financeiro',
    name: 'Financeiro',
    component: () => import('../views/Financeiro.vue'),
    meta: { requiresAuth: true, page: 'financeiro' }
  },
  {
    path: '/financeiro/fluxo-caixa',
    name: 'FinanceiroFluxoCaixa',
    component: () => import('../views/Financeiro.vue'),
    meta: { requiresAuth: true, page: 'financeiro' }
  },
  {
    path: '/financeiro/dre',
    redirect: '/financeiro/dre/analise'
  },
  {
    path: '/financeiro/dre/analise',
    name: 'FinanceiroDreAnalise',
    component: () => import('../views/FinanceiroDre.vue'),
    meta: { requiresAuth: true, page: 'financeiro' }
  },
  {
    path: '/financeiro/dre/cadastros',
    name: 'FinanceiroDreCadastros',
    component: () => import('../views/FinanceiroDre.vue'),
    meta: { requiresAuth: true, page: 'financeiro' }
  },
  {
    path: '/financeiro/dre/planejado',
    name: 'FinanceiroDrePlanejado',
    component: () => import('../views/FinanceiroDre.vue'),
    meta: { requiresAuth: true, page: 'financeiro' }
  },
  {
    path: '/financeiro/dre/logs',
    name: 'FinanceiroDreLogs',
    component: () => import('../views/FinanceiroDre.vue'),
    meta: { requiresAuth: true, page: 'financeiro' }
  },
  {
    path: '/franquias',
    name: 'FinanceiroFranquias',
    component: () => import('../views/Franquias.vue'),
    meta: { requiresAuth: true, page: 'franquias' }
  },
  {
    path: '/produtos',
    name: 'Produtos',
    component: () => import('../views/Produtos.vue'),
    meta: { requiresAuth: true, page: 'produtos' }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  let isAuthenticated = store.getters.isAuthenticated
  let user = store.state.user

  if (isAuthenticated && !user) {
    try {
      await store.dispatch('initializeAuth')
      user = store.state.user
    } catch (error) {
      console.error('Erro ao inicializar autenticação:', error)
    }
    isAuthenticated = store.getters.isAuthenticated
  }

  if (to.meta.requiresAuth && !isAuthenticated) {
    next('/login')
    return
  }

  const mustChangePassword = !!user?.must_change_password
  const isFirstAccessRoute = ['FirstAccess', 'AutoLogin', 'ForceChangePassword'].includes(to.name)

  if (isAuthenticated && mustChangePassword && !isFirstAccessRoute) {
    next({ name: 'FirstAccess' })
    return
  }

  if (to.name === 'FirstAccess' && (!isAuthenticated || !mustChangePassword)) {
    next(isAuthenticated ? '/' : '/login')
    return
  }

  const isFranqueado = user?.user_type === 'franqueado'
  if (isAuthenticated && isFranqueado) {
    const allowedFranqueadoRoutes = new Set([
      'FinanceiroFranquias',
      'Profile',
      'FirstAccess',
      'ForceChangePassword',
      'AutoLogin'
    ])
    if (!allowedFranqueadoRoutes.has(to.name)) {
      next({ name: 'FinanceiroFranquias' })
      return
    }
  }

  if (to.meta.requiresAdmin && !user?.is_staff) {
    // Se não temos o usuário carregado ainda, inicializar primeiro
    if (!user && isAuthenticated) {
      try {
        await store.dispatch('initializeAuth')
        const updatedUser = store.state.user
        if (updatedUser?.is_staff) {
          next()
          return
        }
      } catch (error) {
        console.error('Erro ao inicializar autenticação:', error)
      }
    }
    next('/')
  } else if (to.path === '/login' && isAuthenticated) {
    next('/')
  } else if (to.meta.page && isAuthenticated && to.path !== '/page-management') {
    // Páginas básicas sempre acessíveis
    const basicPages = ['dashboard', 'favorites', 'profile']
    if (basicPages.includes(to.meta.page)) {
      next()
      return
    }
    
    // Verificar permissão da página apenas se não estiver na página de gerenciamento
    // para evitar loops de verificação que podem causar logout
    try {
      await permissionService.loadUserPermissions()
      if (permissionService.hasAccess(to.meta.page)) {
        next()
      } else {
        // Redirecionar para dashboard se não tiver acesso
        next('/')
      }
    } catch (error) {
      console.error('Erro ao verificar permissões:', error)
      // Em caso de erro, permitir acesso (fallback)
      next()
    }
  } else {
    next()
  }
})

export default router 